import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card, Spinner, Modal, Form } from 'react-bootstrap';
import authService from '../services/authService';
import BackToDashboard from '../components/BackToDashboard';

function ColorPaletteSelection() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [palettes, setPalettes] = useState([]);
  const [archetypeInfo, setArchetypeInfo] = useState(null);
  const [selectedPalette, setSelectedPalette] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedPalette, setSavedPalette] = useState(null);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [customColors, setCustomColors] = useState({
    primary: '#3B5998',
    primary_name: 'Primary',
    secondary: '#8B9DC3',
    secondary_name: 'Secondary',
    accent1: '#DFE3EE',
    accent1_name: 'Accent 1',
    accent2: '#F7F7F7',
    accent2_name: 'Accent 2',
    accent3: '#FFFFFF',
    accent3_name: 'Accent 3',
    neutral1: '#2B2B2B',
    neutral1_name: 'Neutral Dark',
    neutral2: '#F5F5F5',
    neutral2_name: 'Neutral Light'
  });

  useEffect(() => {
    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializePage = async () => {
    setLoading(true);

    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Check if user has saved palette
    await fetchSavedPalette();

    setLoading(false);
  };

  const fetchSavedPalette = async () => {
    try {
      const response = await fetch('/api/brand/get-saved-palette', {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSavedPalette(data.palette);

        // Display saved palette as a single option
        setPalettes([data.palette]);
        setSelectedPalette(0);

        // Pre-populate customColors with saved palette values for editing
        if (data.palette) {
          setCustomColors({
            primary: data.palette.primary || '#3B5998',
            primary_name: data.palette.primary_name || 'Primary',
            secondary: data.palette.secondary || '#8B9DC3',
            secondary_name: data.palette.secondary_name || 'Secondary',
            accent1: data.palette.accent1 || '#DFE3EE',
            accent1_name: data.palette.accent1_name || 'Accent 1',
            accent2: data.palette.accent2 || '#F7F7F7',
            accent2_name: data.palette.accent2_name || 'Accent 2',
            accent3: data.palette.accent3 || '#FFFFFF',
            accent3_name: data.palette.accent3_name || 'Accent 3',
            neutral1: data.palette.neutral1 || '#2B2B2B',
            neutral1_name: data.palette.neutral1_name || 'Neutral Dark',
            neutral2: data.palette.neutral2 || '#F5F5F5',
            neutral2_name: data.palette.neutral2_name || 'Neutral Light'
          });
        }

        // Get archetype info
        await fetchArchetypeInfo();
      }
      // If 404, no saved palette - that's okay
    } catch (err) {
      console.log('No saved palette found or error fetching:', err);
    }
  };

  const fetchArchetypeInfo = async () => {
    try {
      const response = await fetch('/api/brand/get-archetypes', {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setArchetypeInfo({
          primary: data.primary?.name,
          secondary: data.secondary?.name,
          tertiary: data.tertiary?.name
        });
      }
    } catch (err) {
      console.log('Error fetching archetypes:', err);
    }
  };

  const handleGeneratePalettes = async () => {
    setGenerating(true);
    setError('');
    setSelectedPalette(null);  // Reset selection when regenerating
    setSavedPalette(null);     // Clear saved state when generating new

    try {
      const response = await fetch('/api/brand/generate-color-palettes', {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setPalettes(data.palettes);
        setArchetypeInfo(data.archetype_info);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to generate color palettes');
      }
    } catch (err) {
      setError('Error generating palettes: ' + err.message);
    }

    setGenerating(false);
  };

  const handleSavePalette = async () => {
    if (selectedPalette === null) return;

    setSaving(true);
    setError('');

    try {
      const palette = palettes[selectedPalette];

      const response = await fetch('/api/brand/save-color-palette', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ palette })
      });

      if (response.ok) {
        // Success! Navigate to customization page
        navigate('/dashboard/palette-customize');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save palette');
      }
    } catch (err) {
      setError('Error saving palette: ' + err.message);
    }

    setSaving(false);
  };

  const handleCustomColorChange = (colorKey, value) => {
    // Validate hex color format
    const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

    setCustomColors(prev => ({
      ...prev,
      [colorKey]: value
    }));
  };

  const handleCustomColorNameChange = (nameKey, value) => {
    setCustomColors(prev => ({
      ...prev,
      [nameKey]: value
    }));
  };

  const isValidHexColor = (color) => {
    const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    return hexRegex.test(color);
  };

  const handleSaveCustomPalette = async () => {
    // Validate all colors
    const colorKeys = ['primary', 'secondary', 'accent1', 'accent2', 'accent3', 'neutral1', 'neutral2'];
    const invalidColors = colorKeys.filter(key => !isValidHexColor(customColors[key]));

    if (invalidColors.length > 0) {
      setError('Please enter valid hex color codes (e.g., #FF5733) for all colors');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const customPalette = {
        ...customColors,
        name: 'Custom Palette',
        description: 'Your custom color palette'
      };

      const response = await fetch('/api/brand/save-color-palette', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ palette: customPalette })
      });

      if (response.ok) {
        // Close modal and navigate to customization page
        setShowCustomModal(false);
        navigate('/dashboard/palette-customize');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save custom palette');
      }
    } catch (err) {
      setError('Error saving custom palette: ' + err.message);
    }

    setSaving(false);
  };

  const ColorSwatch = ({ color, name }) => (
    <div className="text-center mb-2">
      <div
        style={{
          backgroundColor: color,
          height: '60px',
          borderRadius: '8px',
          border: '1px solid #ddd',
          marginBottom: '8px'
        }}
      ></div>
      <div style={{ fontSize: '0.75rem' }}>
        <strong>{name}</strong>
        <br />
        <code style={{ fontSize: '0.7rem' }}>{color}</code>
      </div>
    </div>
  );

  const PaletteCard = ({ palette, index }) => (
    <Card
      className={`mb-4 ${selectedPalette === index ? 'border-primary border-3' : ''}`}
      style={{ cursor: 'pointer' }}
      onClick={() => setSelectedPalette(index)}
    >
      <Card.Header className={selectedPalette === index ? 'bg-primary text-white' : ''}>
        <h5 className="mb-0">
          {selectedPalette === index && '✓ '}
          {palette.name}
        </h5>
      </Card.Header>
      <Card.Body>
        <p className="mb-3">{palette.description}</p>

        <Row className="mb-3">
          <Col xs={6}>
            <small className="text-muted d-block mb-2"><strong>Primary & Secondary</strong></small>
            <Row>
              <Col xs={6}>
                <ColorSwatch color={palette.primary} name={palette.primary_name} />
              </Col>
              <Col xs={6}>
                <ColorSwatch color={palette.secondary} name={palette.secondary_name} />
              </Col>
            </Row>
          </Col>

          <Col xs={6}>
            <small className="text-muted d-block mb-2"><strong>Accents</strong></small>
            <Row>
              <Col xs={4}>
                <ColorSwatch color={palette.accent1} name={palette.accent1_name} />
              </Col>
              <Col xs={4}>
                <ColorSwatch color={palette.accent2} name={palette.accent2_name} />
              </Col>
              <Col xs={4}>
                <ColorSwatch color={palette.accent3} name={palette.accent3_name} />
              </Col>
            </Row>
          </Col>
        </Row>

        <Row>
          <Col>
            <small className="text-muted d-block mb-2"><strong>Neutrals</strong></small>
            <Row>
              <Col xs={6}>
                <ColorSwatch color={palette.neutral1} name={palette.neutral1_name} />
              </Col>
              <Col xs={6}>
                <ColorSwatch color={palette.neutral2} name={palette.neutral2_name} />
              </Col>
            </Row>
          </Col>
        </Row>

        {selectedPalette === index && (
          <Alert variant="primary" className="mt-3 mb-0">
            <small><strong>✓ Selected</strong> - Click "Continue" to use this palette</small>
          </Alert>
        )}
      </Card.Body>
    </Card>
  );

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <p>Loading...</p>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <BackToDashboard module="branding" />
      <Row className="justify-content-center">
        <Col md={12} lg={10}>
          <Card className="shadow">
            <Card.Body className="p-5">
              <h1 className="text-center mb-4">🎨 Choose Your Color Palette</h1>

              {archetypeInfo && (
                <Alert variant="info" className="text-center mb-4">
                  <strong>Your Brand Archetypes:</strong> {archetypeInfo.primary}
                  {archetypeInfo.secondary && ` + ${archetypeInfo.secondary}`}
                  {archetypeInfo.tertiary && ` (with ${archetypeInfo.tertiary} accent)`}
                </Alert>
              )}

              {error && <Alert variant="danger">{error}</Alert>}

              {savedPalette && palettes.length === 1 && (
                <Alert variant="success" className="text-center mb-4">
                  <strong>✓ Your Saved Color Palette</strong> - This is the palette you selected. You can regenerate to see new options or continue to customize.
                </Alert>
              )}

              {palettes.length === 0 ? (
                <div className="text-center py-5">
                  <p className="lead mb-4">
                    Generate AI-powered color palettes based on your brand archetype.
                  </p>
                  <p className="text-muted mb-4">
                    Claude will analyze your brand archetype and create 4-5 unique,
                    professional color palettes designed to embody your brand personality.
                  </p>
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleGeneratePalettes}
                    disabled={generating}
                  >
                    {generating ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Generating Palettes...
                      </>
                    ) : (
                      'Generate Color Palettes →'
                    )}
                  </Button>
                  <div className="mt-3">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => setShowCustomModal(true)}
                      disabled={generating}
                    >
                      Upload My Own Palette
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  {!savedPalette && (
                    <p className="text-center mb-4">
                      Select your favorite palette below. Each palette includes 7 colors designed to work harmoniously together.
                    </p>
                  )}

                  {generating && (
                    <Alert variant="info" className="text-center">
                      <Spinner animation="border" size="sm" className="me-2" />
                      <strong>Generating fresh palettes...</strong> Claude is analyzing your {archetypeInfo.primary}{archetypeInfo.secondary ? ` + ${archetypeInfo.secondary}` : ''} archetype{archetypeInfo.secondary ? 's' : ''}.
                    </Alert>
                  )}

                  <div style={{ opacity: generating ? 0.5 : 1, transition: 'opacity 0.3s' }}>
                    {palettes.map((palette, index) => (
                      <PaletteCard key={index} palette={palette} index={index} />
                    ))}
                  </div>

                  <div className="d-flex justify-content-between align-items-center mt-4">
                    <div>
                      <Button
                        variant="outline-secondary"
                        onClick={handleGeneratePalettes}
                        disabled={generating}
                        className="me-2"
                      >
                        {generating ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Generating...
                          </>
                        ) : (
                          '🔄 Regenerate'
                        )}
                      </Button>
                      {savedPalette && (
                        <Button
                          variant="outline-primary"
                          onClick={() => setShowCustomModal(true)}
                          disabled={generating}
                        >
                          ✏️ Edit Palette
                        </Button>
                      )}
                    </div>
                    <Button
                      variant="primary"
                      size="lg"
                      disabled={selectedPalette === null || saving}
                      onClick={handleSavePalette}
                    >
                      {saving ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Saving Palette...
                        </>
                      ) : (
                        'Save & Continue →'
                      )}
                    </Button>
                  </div>
                </>
              )}

              {palettes.length === 0 && (
                <div className="text-center mt-4">
                  <Button
                    variant="outline-secondary"
                    onClick={() => navigate('/dashboard/brand-results')}
                  >
                    ← Back to Results
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Custom Palette Modal */}
      <Modal show={showCustomModal} onHide={() => setShowCustomModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Enter Your Custom Color Palette</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-4">
            Enter your brand colors as hex codes (e.g., #FF5733). You'll see a preview of each color as you type.
          </p>

          {error && <Alert variant="danger">{error}</Alert>}

          {/* Primary & Secondary */}
          <h6 className="mb-3">Primary & Secondary Colors</h6>
          <Row className="mb-4">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Primary Color Name</Form.Label>
                <Form.Control
                  type="text"
                  value={customColors.primary_name}
                  onChange={(e) => handleCustomColorNameChange('primary_name', e.target.value)}
                  placeholder="e.g., Brand Blue"
                />
              </Form.Group>
              <Form.Group className="mt-2">
                <Form.Label>Primary Color Code</Form.Label>
                <div className="d-flex align-items-center">
                  <div
                    style={{
                      width: '50px',
                      height: '50px',
                      backgroundColor: isValidHexColor(customColors.primary) ? customColors.primary : '#CCCCCC',
                      border: '2px solid #ddd',
                      borderRadius: '8px',
                      marginRight: '10px',
                      flexShrink: 0
                    }}
                  ></div>
                  <Form.Control
                    type="text"
                    value={customColors.primary}
                    onChange={(e) => handleCustomColorChange('primary', e.target.value)}
                    placeholder="#3B5998"
                    isInvalid={customColors.primary && !isValidHexColor(customColors.primary)}
                  />
                </div>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Secondary Color Name</Form.Label>
                <Form.Control
                  type="text"
                  value={customColors.secondary_name}
                  onChange={(e) => handleCustomColorNameChange('secondary_name', e.target.value)}
                  placeholder="e.g., Light Blue"
                />
              </Form.Group>
              <Form.Group className="mt-2">
                <Form.Label>Secondary Color Code</Form.Label>
                <div className="d-flex align-items-center">
                  <div
                    style={{
                      width: '50px',
                      height: '50px',
                      backgroundColor: isValidHexColor(customColors.secondary) ? customColors.secondary : '#CCCCCC',
                      border: '2px solid #ddd',
                      borderRadius: '8px',
                      marginRight: '10px',
                      flexShrink: 0
                    }}
                  ></div>
                  <Form.Control
                    type="text"
                    value={customColors.secondary}
                    onChange={(e) => handleCustomColorChange('secondary', e.target.value)}
                    placeholder="#8B9DC3"
                    isInvalid={customColors.secondary && !isValidHexColor(customColors.secondary)}
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>

          {/* Accent Colors */}
          <h6 className="mb-3">Accent Colors</h6>
          <Row className="mb-4">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Accent 1 Name</Form.Label>
                <Form.Control
                  type="text"
                  value={customColors.accent1_name}
                  onChange={(e) => handleCustomColorNameChange('accent1_name', e.target.value)}
                  placeholder="Accent 1"
                />
              </Form.Group>
              <Form.Group className="mt-2">
                <Form.Label>Color Code</Form.Label>
                <div className="d-flex align-items-center">
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      backgroundColor: isValidHexColor(customColors.accent1) ? customColors.accent1 : '#CCCCCC',
                      border: '2px solid #ddd',
                      borderRadius: '8px',
                      marginRight: '8px',
                      flexShrink: 0
                    }}
                  ></div>
                  <Form.Control
                    type="text"
                    size="sm"
                    value={customColors.accent1}
                    onChange={(e) => handleCustomColorChange('accent1', e.target.value)}
                    placeholder="#DFE3EE"
                    isInvalid={customColors.accent1 && !isValidHexColor(customColors.accent1)}
                  />
                </div>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Accent 2 Name</Form.Label>
                <Form.Control
                  type="text"
                  value={customColors.accent2_name}
                  onChange={(e) => handleCustomColorNameChange('accent2_name', e.target.value)}
                  placeholder="Accent 2"
                />
              </Form.Group>
              <Form.Group className="mt-2">
                <Form.Label>Color Code</Form.Label>
                <div className="d-flex align-items-center">
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      backgroundColor: isValidHexColor(customColors.accent2) ? customColors.accent2 : '#CCCCCC',
                      border: '2px solid #ddd',
                      borderRadius: '8px',
                      marginRight: '8px',
                      flexShrink: 0
                    }}
                  ></div>
                  <Form.Control
                    type="text"
                    size="sm"
                    value={customColors.accent2}
                    onChange={(e) => handleCustomColorChange('accent2', e.target.value)}
                    placeholder="#F7F7F7"
                    isInvalid={customColors.accent2 && !isValidHexColor(customColors.accent2)}
                  />
                </div>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Accent 3 Name</Form.Label>
                <Form.Control
                  type="text"
                  value={customColors.accent3_name}
                  onChange={(e) => handleCustomColorNameChange('accent3_name', e.target.value)}
                  placeholder="Accent 3"
                />
              </Form.Group>
              <Form.Group className="mt-2">
                <Form.Label>Color Code</Form.Label>
                <div className="d-flex align-items-center">
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      backgroundColor: isValidHexColor(customColors.accent3) ? customColors.accent3 : '#CCCCCC',
                      border: '2px solid #ddd',
                      borderRadius: '8px',
                      marginRight: '8px',
                      flexShrink: 0
                    }}
                  ></div>
                  <Form.Control
                    type="text"
                    size="sm"
                    value={customColors.accent3}
                    onChange={(e) => handleCustomColorChange('accent3', e.target.value)}
                    placeholder="#FFFFFF"
                    isInvalid={customColors.accent3 && !isValidHexColor(customColors.accent3)}
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>

          {/* Neutral Colors */}
          <h6 className="mb-3">Neutral Colors</h6>
          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Neutral Dark Name</Form.Label>
                <Form.Control
                  type="text"
                  value={customColors.neutral1_name}
                  onChange={(e) => handleCustomColorNameChange('neutral1_name', e.target.value)}
                  placeholder="e.g., Dark Gray"
                />
              </Form.Group>
              <Form.Group className="mt-2">
                <Form.Label>Color Code</Form.Label>
                <div className="d-flex align-items-center">
                  <div
                    style={{
                      width: '50px',
                      height: '50px',
                      backgroundColor: isValidHexColor(customColors.neutral1) ? customColors.neutral1 : '#CCCCCC',
                      border: '2px solid #ddd',
                      borderRadius: '8px',
                      marginRight: '10px',
                      flexShrink: 0
                    }}
                  ></div>
                  <Form.Control
                    type="text"
                    value={customColors.neutral1}
                    onChange={(e) => handleCustomColorChange('neutral1', e.target.value)}
                    placeholder="#2B2B2B"
                    isInvalid={customColors.neutral1 && !isValidHexColor(customColors.neutral1)}
                  />
                </div>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Neutral Light Name</Form.Label>
                <Form.Control
                  type="text"
                  value={customColors.neutral2_name}
                  onChange={(e) => handleCustomColorNameChange('neutral2_name', e.target.value)}
                  placeholder="e.g., Light Gray"
                />
              </Form.Group>
              <Form.Group className="mt-2">
                <Form.Label>Color Code</Form.Label>
                <div className="d-flex align-items-center">
                  <div
                    style={{
                      width: '50px',
                      height: '50px',
                      backgroundColor: isValidHexColor(customColors.neutral2) ? customColors.neutral2 : '#CCCCCC',
                      border: '2px solid #ddd',
                      borderRadius: '8px',
                      marginRight: '10px',
                      flexShrink: 0
                    }}
                  ></div>
                  <Form.Control
                    type="text"
                    value={customColors.neutral2}
                    onChange={(e) => handleCustomColorChange('neutral2', e.target.value)}
                    placeholder="#F5F5F5"
                    isInvalid={customColors.neutral2 && !isValidHexColor(customColors.neutral2)}
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowCustomModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveCustomPalette} disabled={saving}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              'Save & Continue →'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default ColorPaletteSelection;
